import { skyFlightCourseProfile } from '../../../packages/domain/src/113-sky-flight-courses.mjs';
import { SKY_STAGES } from '../../../packages/domain/src/76-sky-campaign.mjs';

const number = (sector) => String(sector + 1).padStart(2, '0');
const mode = (stage) =>
  skyFlightCourseProfile(stage)?.name ??
  (stage.kind === 'bonus'
    ? 'RING BONANZA'
    : stage.kind === 'rush'
      ? 'GUARDIAN GAUNTLET'
      : stage.encounterId === 'gilded-leviathan'
        ? 'CLOUD ASCENT / HULL RUN'
        : 'FLIGHT / GUARDIAN');

/** Session-only stage selection projects the active campaign. Native radios own
 * keyboard selection; the controller owns run resets and refuses in-flight edits.
 * No storage, unlock claims, speculative levels or model downloads belong here. */
export function createSkyLevelAtlas({ e, listen, select }) {
  const element = e('details', 'sky-level-atlas');
  const summary = e('summary', 'sky-atlas-summary');
  const badge = e('span', 'sky-atlas-number');
  const heading = e('span', 'sky-atlas-heading');
  const label = e('span', 'sky-atlas-kicker', 'CHOOSE YOUR FLIGHT');
  const title = e('strong', 'sky-atlas-current');
  const metadata = e('span', 'sky-atlas-meta');
  const chevron = e('span', 'sky-atlas-chevron', '⌄');
  chevron.setAttribute('aria-hidden', 'true');
  heading.append(label, title, metadata);
  summary.append(badge, heading, chevron);
  const form = e('form', 'sky-atlas-form');
  const modeFieldset = e('fieldset', 'sky-atlas-modes');
  modeFieldset.append(e('legend', 'sky-atlas-legend', 'Run mode'));
  const modeChoices = [
    ['full', 'Full run', 'Fly the approach, collect upgrades, then face the guardian.'],
    [
      'boss',
      'Straight to boss',
      'Start at the guardian arrival. No approach rewards or first-pass bonus.',
    ],
  ].map(([value, name, help]) => {
    const card = e('label', 'sky-atlas-mode');
    const input = e('input', 'sky-atlas-mode-radio');
    input.type = 'radio';
    input.name = 'sky-flight-mode';
    input.value = value;
    input.setAttribute('aria-label', `${name}. ${help}`);
    card.append(input, e('strong', '', name), e('span', 'sky-atlas-mode-help', help));
    listen(input, 'change', () => {
      if (input.checked && !input.disabled) select(null, value);
    });
    modeFieldset.append(card);
    return { input, card };
  });
  const fieldset = e('fieldset', 'sky-atlas-grid');
  const legend = e('legend', 'sky-atlas-legend', 'Flight atlas');
  const note = e(
    'p',
    'sky-atlas-note',
    'OPEN FLIGHT TEST · Start at any level with standard equipment. Authored guardians and signature encounters remain under review. Worlds marked as previews are still being built and visually tested.',
  );
  fieldset.append(legend);
  const choices = SKY_STAGES.map((stage, sector) => {
    const card = e('label', 'sky-atlas-card');
    card.style.setProperty('--sky-route-accent', stage.accent);
    card.style.setProperty('--sky-route-ground', stage.ground);
    card.style.setProperty('--sky-route-sky', stage.sky);
    const input = e('input', 'sky-atlas-radio');
    input.type = 'radio';
    input.name = 'sky-flight-stage';
    input.value = String(sector);
    input.dataset.sector = String(sector);
    input.setAttribute('aria-label', `Level ${sector + 1}: ${stage.name}. ${mode(stage)}.`);
    const marker = e('span', 'sky-atlas-marker', number(sector));
    marker.setAttribute('aria-hidden', 'true');
    const name = e('strong', 'sky-atlas-name', stage.name);
    const category = e('span', 'sky-atlas-category', mode(stage));
    const guardian = e(
      'span',
      'sky-atlas-guardian',
      stage.authoredBoss
        ? `AUTHORED GUARDIAN · ${stage.bossName}`
        : stage.bossPreview
          ? `PRACTICE GUARDIAN · ${stage.previewBossName}`
          : stage.bossName,
    );
    const selected = e('span', 'sky-atlas-selected', '✓ SELECTED');
    selected.setAttribute('aria-hidden', 'true');
    card.append(input, marker, name, category, guardian, selected);
    fieldset.append(card);
    listen(input, 'change', () => {
      if (input.checked && !input.disabled) select(sector);
    });
    return { input, card };
  });
  const done = e('button', 'sky-button sky-atlas-done', 'Ready to launch');
  done.type = 'button';
  const close = () => {
    element.open = false;
    summary.focus();
  };
  listen(done, 'click', close);
  listen(element, 'keydown', (event) => {
    if (event.key === 'Escape' && element.open) {
      event.preventDefault();
      close();
    }
  });
  listen(form, 'submit', (event) => event.preventDefault());
  form.append(note, modeFieldset, fieldset, done);
  element.append(summary, form);
  return {
    element,
    paint(sector, visible, pending, startMode = 'full') {
      element.hidden = !visible;
      if (!visible) element.open = false;
      const stage = SKY_STAGES[sector];
      badge.textContent = number(sector);
      title.textContent = stage.name;
      metadata.textContent = `${startMode === 'boss' ? 'STRAIGHT TO BOSS' : 'FULL RUN'} · ${stage.bossPreview ? 'WORLD PREVIEW' : 'FULL ENCOUNTER'}`;
      for (const choice of modeChoices) {
        choice.input.checked = choice.input.value === startMode;
        choice.input.disabled = pending;
        choice.card.dataset.selected = String(choice.input.checked);
      }
      element.style.setProperty('--sky-route-accent', stage.accent);
      for (const [index, choice] of choices.entries()) {
        choice.input.checked = index === sector;
        choice.input.disabled = pending;
        choice.card.dataset.selected = String(index === sector);
      }
    },
  };
}

/** Paint the held flight menu from canonical run and preload state. */
export function paintSkyFlightMenu({
  state,
  world,
  description,
  sequence,
  sequencePaused,
  pending,
  graphicsError,
  purchaseMessage,
  title,
  eyebrow,
  copy,
  start,
  restart,
  status,
}) {
  const intermission = state.status === 'intermission';
  const ended = ['over', 'complete'].includes(state.status);
  title.textContent =
    state.status === 'ready'
      ? 'SKY RIDER'
      : intermission
        ? 'SECTOR CLEAR'
        : state.status === 'paused'
          ? 'FLIGHT PAUSED'
          : state.status === 'complete'
            ? 'FRONTIER RESTORED'
            : 'FLIGHT ENDED';
  eyebrow.textContent = intermission
    ? `NEXT / ${world.name}`
    : ended
      ? `${state.score.toLocaleString()} POINTS · ${state.kills} TARGETS`
      : state.status === 'paused'
        ? 'SYSTEM ON HOLD / PILOT CONTROL'
        : 'OPEN FLIGHT TEST / EVERY LEVEL AVAILABLE';
  copy.textContent = intermission
    ? `Next: ${world.name}. Install earned upgrades or save your salvage.`
    : state.status === 'ready'
      ? world.kind === 'bonus'
        ? 'Steer through the gates. Build a clean chain to earn salvage for upgrades. WASD moves; the left pad steers.'
        : `${state.startMode === 'boss' ? 'Straight to boss: standard equipment, no approach rewards or first-pass bonus. ' : ''}${world.description} ${world.signaturePreview ? 'Authored guardian; encounter under review. ' : world.bossPreview ? 'Practice guardian; signature battle in development. ' : ''}${description.hint}.`
      : ended
        ? `${state.event}. ${SKY_STAGES.length} worlds, earned upgrades, one more flight. This run stays private.`
        : description.label + ' Resume when you are ready.';
  start.textContent = sequencePaused
    ? 'Resume sequence'
    : pending
      ? 'Preparing flight…'
      : graphicsError
        ? 'Retry graphics'
        : intermission
          ? 'Launch next stage'
          : state.status === 'paused'
            ? 'Resume flight'
            : state.startMode === 'boss'
              ? 'Launch boss encounter'
              : 'Launch selected level';
  start.disabled = pending;
  restart.hidden = (state.status === 'ready' && !sequence) || ended;
  restart.textContent =
    sequencePaused && state.status === 'ready' ? 'Cancel launch' : 'New expedition';
  status.textContent = graphicsError
    ? 'WebGL is unavailable or was interrupted. Retry to restore this paused flight.'
    : purchaseMessage;
}
